import React, { Component } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { I18n } from "react-redux-i18n";
import TopBar from "../components/TopBar";

const { normalizeTag } = require("./guidelinesTagUtils");
const { getTagLabel, getAllTagLabels } = require("./videoGuideTags");
const VIDEO_GUIDE_ENTRIES = require("./videoGuideEntries");

const AREAS = {
  sales: { en: "Sales / POS", hu: "Értékesítés / kassza", de: "Verkauf / Kasse", cn: "销售 / 收银" },
  tables: { en: "Tables & reservations", hu: "Asztalok és foglalások", de: "Tische & Reservierungen", cn: "桌位与预订" },
  kitchen: { en: "Kitchen & serving", hu: "Konyha és felszolgálás", de: "Küche & Service", cn: "后厨与出餐" },
  delivery: { en: "Delivery & pickup", hu: "Kiszállítás és elvitel", de: "Lieferung & Abholung", cn: "外送与自取" },
  kiosk: { en: "Kiosk", hu: "Kioszk", de: "Kiosk", cn: "自助点餐机" },
  menu: { en: "Menu & products", hu: "Étlap és termékek", de: "Speisekarte & Produkte", cn: "菜单与产品" },
  chatgpt: { en: "ChatGPT", hu: "ChatGPT", de: "ChatGPT", cn: "ChatGPT" },
  stock: { en: "Stock management", hu: "Készletkezelés", de: "Lagerverwaltung", cn: "库存管理" },
  reports: { en: "Reports", hu: "Riportok", de: "Berichte", cn: "报表" },
  administration: { en: "Administration", hu: "Adminisztráció", de: "Verwaltung", cn: "管理" },
  integrations: { en: "Integrations", hu: "Integrációk", de: "Integrationen", cn: "集成" },
  marketing: { en: "Guests & marketing", hu: "Vendégek és marketing", de: "Gäste & Marketing", cn: "顾客与营销" },
  support: { en: "Support", hu: "Támogatás", de: "Support", cn: "支持" },
  superadmin: { en: "Super admin", hu: "Szuperadmin", de: "Superadmin", cn: "超级管理员" }
};

const MAX_TAG_CHIPS = 20;

const translateWithFallback = (key, fallback) => {
  const translated = I18n.t(key);
  return translated === key ? fallback : translated;
};

export const isSuperAdminStatus = (myStatus) =>
  (myStatus?.roles || []).some(role => String(role.role || "").toLowerCase() === "superadmin");

// Accepts a bare 11 character id or a youtube.com / youtu.be URL and returns the id, or null when it is not valid.
export const parseYoutubeId = (value) => {
  if (!value || typeof value !== "string") return null;
  const text = value.trim();
  if (/^[\w-]{11}$/.test(text)) return text;
  const match = text.match(/^https:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})(?:[?&#].*)?$/i);
  return match ? match[1] : null;
};

class VideoGuides extends Component {
  state = {
    searchTerm: "",
    selectedTags: [],
    selectedArea: "",
    playing: {},
    largeId: null,
    scriptsOpen: {},
    scripts: null
  };

  getLanguage = () => localStorage.language || "en";

  getText = (entry, field) => {
    const i18n = entry[`${field}_i18n`];
    if (i18n && typeof i18n === "object") {
      return i18n[this.getLanguage()] || i18n.en || "";
    }
    return entry[field] || "";
  };

  getTagLabel = (tag) => getTagLabel(tag, this.getLanguage());

  getAreaLabel = (area) => {
    const labels = AREAS[area];
    return labels ? (labels[this.getLanguage()] || labels.en) : area;
  };

  getEntries = () => VIDEO_GUIDE_ENTRIES.map(entry => ({
    id: entry.id,
    area: entry.area,
    title: this.getText(entry, "title"),
    summary: this.getText(entry, "summary"),
    tags: Array.isArray(entry.tags) ? entry.tags.map(normalizeTag).filter(Boolean) : [],
    youtubeId: parseYoutubeId(entry.youtubeId),
    createdAt: entry.createdAt,
    playwright: entry.playwright
  }));

  filterEntries = (entries) => {
    const term = this.state.searchTerm.trim().toLowerCase();
    const { selectedTags, selectedArea } = this.state;
    return entries.filter(entry => {
      if (selectedArea && entry.area !== selectedArea) return false;
      const tagsOk = selectedTags.length === 0 || selectedTags.some(tag => entry.tags.includes(tag));
      const target = [entry.title, entry.summary, ...entry.tags.flatMap(getAllTagLabels)].join(" ").toLowerCase();
      return tagsOk && (!term || target.includes(term));
    });
  };

  toggleTag = (tag) => this.setState(prev => ({
    selectedTags: prev.selectedTags.includes(tag) ? prev.selectedTags.filter(t => t !== tag) : [...prev.selectedTags, tag]
  }));

  selectArea = (area) => this.setState(prev => ({ selectedArea: prev.selectedArea === area ? "" : area, selectedTags: [] }));

  // Same logic as the guidelines: counts of the currently listed entries, most used first, top 20.
  getTagSummary = (entries = []) => {
    const counts = {};
    entries.forEach(entry => entry.tags.forEach(tag => { counts[tag] = (counts[tag] || 0) + 1; }));
    return Object.entries(counts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag))
      .slice(0, MAX_TAG_CHIPS);
  };

  play = (id) => this.setState(prev => ({ playing: { ...prev.playing, [id]: true } }));

  // YouTube chooses the stream quality from the size of the player, so the small inline player
  // gets 360p/480p. The large player is big enough for 1080p.
  openLarge = (youtubeId) => this.setState({ largeId: youtubeId });

  closeLarge = () => this.setState({ largeId: null });

  renderLargePlayer = () => {
    const id = this.state.largeId;
    if (!id) return null;
    return (
      <div
        onClick={this.closeLarge}
        style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <button
          type="button"
          onClick={this.closeLarge}
          aria-label={translateWithFallback("admin_local.video_guides_close", "Close")}
          style={{ position: "absolute", top: 12, right: 16, background: "transparent", border: 0, color: "#fff", fontSize: 34, lineHeight: 1, cursor: "pointer" }}
        >&times;</button>
        <div onClick={e => e.stopPropagation()} style={{ width: "min(96vw, calc(92vh * 16 / 9))", aspectRatio: "16 / 9" }}>
          <iframe
            title="video"
            src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&vq=hd1080&hd=1`}
            style={{ width: "100%", height: "100%", border: 0 }}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
      </div>
    );
  };

  // The sources of the Playwright scripts (npm run video:export in eatwithme-playwright) are loaded on the first click only.
  toggleScript = (id) => {
    this.setState(prev => ({ scriptsOpen: { ...prev.scriptsOpen, [id]: !prev.scriptsOpen[id] } }));
    if (!this.state.scripts) {
      import("./videoGuideScripts.json")
        .then(module => this.setState({ scripts: module.default || module }))
        .catch(() => this.setState({ scripts: {} }));
    }
  };

  renderScript = (entry) => {
    if (!entry.playwright) return null;
    const open = !!this.state.scriptsOpen[entry.id];
    const source = this.state.scripts ? this.state.scripts[entry.id] : null;
    return (
      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          onClick={() => this.toggleScript(entry.id)}
          style={{ background: "#fffdf8", color: "#3e3527", border: "1px solid #3e3527", borderRadius: 8, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
        >
          {open
            ? translateWithFallback("admin_local.video_guides_hide_script", "Hide script")
            : translateWithFallback("admin_local.video_guides_show_script", "Show script")}
        </button>
        {open ? (
          <pre style={{ margin: "8px 0 0 0", padding: 12, maxHeight: 420, overflow: "auto", background: "#2b2620", color: "#f3ead7", borderRadius: 8, fontSize: 12, lineHeight: 1.45 }}>
            {this.state.scripts === null
              ? "..."
              : source || translateWithFallback("admin_local.video_guides_script_missing", "The script is not exported yet. Run: npm run video:export")}
          </pre>
        ) : null}
      </div>
    );
  };

  renderVideo = (entry) => {
    if (!entry.youtubeId) {
      return (
        <div style={{ marginTop: 10, padding: 18, textAlign: "center", color: "#7a6a54", background: "#f8f1e4", borderRadius: 10 }}>
          {translateWithFallback("admin_local.video_guides_coming_soon", "Video coming soon")}
        </div>
      );
    }

    if (this.state.playing[entry.id]) {
      return (
        <div>
          <div style={{ marginTop: 10, position: "relative", paddingBottom: "56.25%", height: 0 }}>
            <iframe
              title={entry.title}
              src={`https://www.youtube-nocookie.com/embed/${entry.youtubeId}?autoplay=1&rel=0&vq=hd1080&hd=1`}
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "1px solid #e3d8c4", borderRadius: 10 }}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 12, fontSize: 13 }}>
            <button type="button" onClick={() => this.openLarge(entry.youtubeId)} style={{ background: "none", border: 0, padding: 0, color: "#195d3a", cursor: "pointer", textDecoration: "underline" }}>
              {translateWithFallback("admin_local.video_guides_large_player", "Large player (HD)")}
            </button>
            <a href={`https://www.youtube.com/watch?v=${entry.youtubeId}`} target="_blank" rel="noopener noreferrer" style={{ color: "#195d3a" }}>
              {translateWithFallback("admin_local.video_guides_open_youtube", "Open on YouTube")}
            </a>
          </div>
        </div>
      );
    }

    // The player (and the cookies of YouTube) is only loaded after the click.
    return (
      <button
        type="button"
        onClick={() => this.play(entry.id)}
        aria-label={`${translateWithFallback("admin_local.video_guides_play", "Play video")}: ${entry.title}`}
        style={{
          marginTop: 10, position: "relative", display: "block", width: "100%", padding: 0, border: "1px solid #e3d8c4",
          borderRadius: 10, overflow: "hidden", cursor: "pointer", background: "#000", aspectRatio: "16 / 9"
        }}
      >
        <img
          src={`https://i.ytimg.com/vi/${entry.youtubeId}/maxresdefault.jpg`}
          onError={(e) => {
            // maxresdefault does not exist until YouTube has finished the HD processing.
            const fallback = `https://i.ytimg.com/vi/${entry.youtubeId}/hqdefault.jpg`;
            if (e.currentTarget.src !== fallback) {
              e.currentTarget.src = fallback;
            }
          }}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: 0.85 }}
        />
        <span style={{
          position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: 68, height: 48,
          borderRadius: 12, background: "#e62117", color: "#fff", fontSize: 24, lineHeight: "48px"
        }}>&#9654;</span>
      </button>
    );
  };

  renderCard = (entry) => (
    <div
      key={entry.id}
      style={{ border: "1px solid #e7dfcf", borderRadius: 12, padding: 16, marginBottom: 14, background: "#fffdf8", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
    >
      {entry.area ? <div style={{ color: "#8a7960", fontSize: 12, marginBottom: 4 }}>{this.getAreaLabel(entry.area)}</div> : null}
      <h3 style={{ margin: 0, color: "#3e3527", fontSize: 19 }}>{entry.title}</h3>
      {entry.summary ? <p style={{ margin: "10px 0 0 0", color: "#665842" }}>{entry.summary}</p> : null}
      {this.renderVideo(entry)}
      {entry.tags.length ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {entry.tags.map(tag => {
            const selected = this.state.selectedTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => this.toggleTag(tag)}
                style={{
                  background: selected ? "#dff3e9" : "#f8f1e4", color: selected ? "#195d3a" : "#6f614d",
                  border: selected ? "1px solid #9fd0ad" : "1px solid transparent", borderRadius: 999,
                  padding: "4px 8px", fontSize: 12, cursor: "pointer"
                }}
              >
                #{this.getTagLabel(tag)}
              </button>
            );
          })}
        </div>
      ) : null}
      {entry.playwright ? (
        <div style={{ marginTop: 10, color: "#8a7960", fontSize: 12 }}>
          {translateWithFallback("admin_local.video_guides_recorded_with", "Playwright script")}: <code>{entry.playwright.script}</code> ({entry.playwright.spec})
        </div>
      ) : null}
      {this.renderScript(entry)}
    </div>
  );

  render() {
    if (!isSuperAdminStatus(this.props.myStatus)) {
      return <Redirect to="/" />;
    }

    const title = translateWithFallback("admin_local.menus.video_guides", "Video guide");
    const allEntries = this.getEntries();
    const entries = this.filterEntries(allEntries);
    const areas = Object.keys(AREAS).filter(area => allEntries.some(entry => entry.area === area));
    const summary = this.getTagSummary(entries);
    const tags = this.state.selectedTags.length > 0
      ? summary.filter(item => this.state.selectedTags.includes(item.tag)).concat(summary.filter(item => !this.state.selectedTags.includes(item.tag)))
      : summary;

    return (
      <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <TopBar {...this.props} />
        <div style={{ flex: "1 1 auto", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ maxWidth: 940, margin: "0 auto", padding: "18px 16px 24px 16px" }}>
            <h2 style={{ marginTop: 0, color: "#3e3527" }}>{title}</h2>
            <p style={{ color: "#6f614d", marginTop: 0 }}>
              {translateWithFallback("admin_local.video_guides_intro", "Short videos showing how to do the most common tasks.")}
            </p>
            <input
              type="text"
              value={this.state.searchTerm}
              onChange={e => this.setState({ searchTerm: e.target.value })}
              placeholder={translateWithFallback("admin_local.video_guides_search_placeholder", "Search title, summary or tags")}
              style={{
                width: "100%", minHeight: 42, border: "1px solid #d9c9a7", borderRadius: 10, padding: "9px 12px", color: "#3e3527",
                background: "#fff", outline: "none", boxSizing: "border-box", marginBottom: 12
              }}
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              {areas.map(area => {
                const selected = this.state.selectedArea === area;
                return (
                  <button
                    key={area}
                    type="button"
                    onClick={() => this.selectArea(area)}
                    style={{
                      background: selected ? "#3e3527" : "#fffdf8", color: selected ? "#fff" : "#3e3527",
                      border: "1px solid #3e3527", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer"
                    }}
                  >
                    {this.getAreaLabel(area)}
                  </button>
                );
              })}
            </div>
            {tags.length ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                {tags.map(({ tag, count }) => {
                  const selected = this.state.selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => this.toggleTag(tag)}
                      style={{
                        background: selected ? "#dff3e9" : "#f4ead8", color: "#5f503c",
                        border: selected ? "1px solid #98d3ae" : "1px solid #e3d7bf", borderRadius: 999,
                        padding: "5px 10px", fontSize: 12, cursor: "pointer"
                      }}
                    >
                      #{this.getTagLabel(tag)} <span style={{ opacity: 0.8 }}>({count})</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
            <div style={{ color: "#6f614d", marginBottom: 12 }}>
              {translateWithFallback("admin_local.guidelines_whats_new_results", "Results")}: {entries.length} / {allEntries.length}
            </div>
            {entries.length === 0 ? (
              <div style={{ color: "#6f614d", padding: "18px 0" }}>
                {translateWithFallback("admin_local.video_guides_empty", "No video guides match your search.")}
              </div>
            ) : entries.map(this.renderCard)}
          </div>
        </div>
        {this.renderLargePlayer()}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  myStatus: state.rootReducer.myStatus,
  rbac: state.rootReducer.rbac
});

export default connect(mapStateToProps)(VideoGuides);
